import type { BillingAccount } from '@arcnet/contracts';
import { useSession } from '../../lib/auth/session.js';
import { useSetAutoSuspend } from '../../lib/api/hooks-subscriptions.js';
import { ApiClientError } from '../../lib/api/client.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';

/**
 * Whether an overdue invoice suspends this client (docs/28 B3): 21 days after the due
 * date, a week after the last reminder. Staff decide; the client never can. Rendered
 * inside the account's details list.
 */
export function OverdueSetting({ tenantId, account }: { tenantId: string; account: BillingAccount }) {
  const can = useSession((s) => s.can);
  const save = useSetAutoSuspend(tenantId);
  const step = useStepUp();
  const err = save.error instanceof ApiClientError && save.error.code !== 'auth.step_up_required'
    ? save.error : null;

  const toggle = () => void step.run(() => save.mutateAsync(!account.auto_suspend)).catch(() => undefined);

  return (
    <>
      <dt>Overdue invoices</dt>
      <dd>
        {account.auto_suspend ? 'Suspended 21 days after the due date' : 'Never suspended'}
        {account.suspended_for_overdue && <span className="arc-subtle">, suspended now</span>}
        {can('billing:write') && (
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" style={{ marginLeft: 8 }}
            disabled={save.isPending} onClick={toggle}>
            {account.auto_suspend ? 'Never suspend' : 'Suspend when overdue'}
          </button>
        )}
        <StepUpPrompt step={step} what="Changing what happens to overdue invoices" />
        {err && <div className="arc-hint" role="alert">{err.message}</div>}
      </dd>
    </>
  );
}
